import { useState } from 'react'
import { DataTableBlock, ListPage } from '@/components/templates/list-page'
import { useViewOnly } from '@/components/templates/app-shell'
import { Alert } from '@/components/ui/alert'
import { AddConnectionFlow } from './AddConnectionSheet'
import { ConnectionAttention } from './connections/ConnectionAttention'
import { ConnectionActionDialog, connectionActions } from './connections/ConnectionActions'
import { ConnectionsToolbar } from './connections/ConnectionsToolbar'
import { connectionColumns } from './connections/columns'
import { connectionPath, type Connection } from './connections/types'
import {
  useConnectionAction,
  useConnectionsList,
  listSummary,
  type ConnectionCommand,
} from './connections/useConnectionsList'

export function ConnectionsListPage() {
  const state = useConnectionsList()
  const viewOnly = useViewOnly()
  const [adding, setAdding] = useState(false)
  const mutation = useConnectionAction()
  const [command, setCommand] = useState<ConnectionCommand | null>(null)
  const { attention, unmatchedTotal, emptyTitle } = listSummary(state.summary.data?.connections)
  const restore = viewOnly
    ? undefined
    : (row: Connection) => mutation.mutate({ row, action: 'restore' })
  const failed = state.rows.isError || state.summary.isError
  const loading = state.rows.isPending || state.summary.isPending
  return (
    <>
      <ListPage<Connection>
        title="Connections"
        primaryAction={{ label: 'Add connection', onClick: () => setAdding(true) }}
        search={{
          value: state.search,
          onChange: state.setSearch,
          placeholder: 'Search connections',
        }}
        toolbar={<ConnectionsToolbar state={state} unmatchedTotal={unmatchedTotal} />}
      >
        {mutation.error && !command && <Alert>{mutation.error.message}</Alert>}
        {attention.length > 0 && (
          <div className="space-y-2 border-b border-border p-3">
            {attention.map((row) => (
              <ConnectionAttention
                key={row.id}
                row={row}
                pending={mutation.isPending}
                onSync={() => mutation.mutate({ row, action: 'sync_now' })}
              />
            ))}
          </div>
        )}
        <DataTableBlock
          table={{
            cellClassName: 'py-0',
            rows: state.rows.data?.connections ?? [],
            columns: connectionColumns(state.sortable, restore, mutation.isPending),
            getRowId: (row) => row.id,
            getRowLabel: (row) => row.display_name,
            onRowOpen: (row) => window.location.assign(connectionPath(row)),
            actions: (row) =>
              connectionActions(row, mutation.mutate, (next) => {
                mutation.reset()
                setCommand(next)
              }),
            state: tableState(failed, loading),
            loadingTitle: 'Loading connections…',
            errorTitle: 'Could not load connections',
            errorDescription: 'Refresh the page to try again.',
            emptyTitle,
          }}
        />
      </ListPage>
      <AddConnectionFlow adding={adding} onClose={() => setAdding(false)} />
      {command && (
        <ConnectionActionDialog
          key={`${command.row.id}-${command.action}`}
          command={command}
          mutation={mutation}
          onClose={() => {
            mutation.reset()
            setCommand(null)
          }}
        />
      )}
    </>
  )
}
function tableState(failed: boolean, loading: boolean) {
  if (failed) return 'error'
  return loading ? 'loading' : 'ready'
}
